/* ============================================================
   ERO NUTRITION - STARFIELD (Launch Workbook 6.14.17 / 6.14.27,
   tile engine locked Sep 30 2026)

   Four fixed depth layers under every page (snippets/ero-starfield.liquid):
   - L1 / L2: SVG tile backgrounds in assets/ero-starfield.css. This file
     sizes their bottom edge from the page length so they never run out.
   - L3 / L4: stars drawn once, from a fixed seed on a 256px cell grid, into
     one small SVG tile per layer (screen width rounded up to 256, min 512,
     by CONFIG.tileH tall). The tile repeats as the layer background, and
     each layer is only one viewport plus one tile tall. Same device, same
     sky, every visit.
   - The L3 / L4 drift wraps every tileH px (ero-starfield.css), so layer
     height and graphics memory never grow with page length. Page-tall
     star layers dropped sections and made stars pop on iPhone (6.14.27).
   - Drift is measured against the page's max scroll (--sf-max, and
     --sf-iter for L3 / L4), which this file writes. Those writes happen
     inside the ResizeObserver callback, which runs after layout and
     before paint, so when the page grows or shrinks (FAQ items, tabs,
     reviews, images) the new length and the new drift land in the same
     frame and no star moves (6.14.29). Only the tile redraw is
     debounced.
   - 6.14.28 (Oct 1 2026) tried a fixed 0 to 100000px scroll range
     instead. On iPhone (iOS 26.6.2) that made the stars step and jitter
     during touch scrolling, worst on short flicks; Taylor's A/B on the
     dev theme confirmed the max-scroll engine is smooth. Do not bring
     the fixed range back.
   - Brightness spread follows a real sky: many faint, few bright. The
     brightest stars get a soft halo. Star colour tints (real star
     colours, Mitchell Charity table) are OFF by default: CONFIG.color.
   - Motion: CSS scroll-driven animations run the parallax in step with
     the scroll, at both breakpoints. Browsers without scroll timelines
     (older Firefox, iOS below 26) fall back to a requestAnimationFrame
     scroll listener in this file.
   - Visitors with prefers-reduced-motion get a still field.
   Tuning knobs live in CONFIG: density, mix, halo, color, tileH.
   ============================================================ */

(function () {
  var CONFIG = {
    seed: 20260930,
    cell: 256,
    // mix rows: [share, radiusMin, radiusMax, opacityMin, opacityMax]
    // density: stars per 100,000 square px of layer area
    layers: {
      l3: {
        selector: '.ero-starfield__layer-3',
        index: 3,
        factor: 0.25,
        density: 8,
        mix: [
          [0.70, 0.50, 0.80, 0.35, 0.60],
          [0.25, 0.80, 1.10, 0.60, 0.85],
          [0.05, 1.10, 1.40, 0.85, 1.00]
        ]
      },
      l4: {
        selector: '.ero-starfield__layer-4',
        index: 4,
        factor: 0.55,
        density: 5,
        mix: [
          [0.60, 0.60, 0.90, 0.45, 0.70],
          [0.30, 0.90, 1.30, 0.70, 0.95],
          [0.09, 1.30, 1.70, 0.95, 1.00],
          [0.01, 1.70, 2.10, 1.00, 1.00]
        ]
      }
    },
    tileFactors: { d1: 0.02, d2: 0.08 },
    // L3/L4 tile height. Must match the 1024px in ero-starfield.css
    // (ero-sf-wrap and the L3/L4 layer height).
    tileH: 1024,
    haloMinRadius: 1.30,
    haloScale: 4,
    haloAlpha: 0.35,
    colorMinOpacity: 0.95,
    // Real star colours (Mitchell Charity, "What color are the stars?"):
    // F, G, A, B, K, M with weights. Applied only when CONFIG.color is
    // true, and only to stars at or above colorMinOpacity.
    color: false,
    tints: [
      ['#f8f7ff', 45],
      ['#fff4ea', 20],
      ['#cad7ff', 15],
      ['#aabfff', 8],
      ['#ffd2a1', 10],
      ['#ffcc6f', 2]
    ]
  };

  var docEl = document.documentElement;
  var tintTotal = 0;
  CONFIG.tints.forEach(function (t) { tintTotal += t[1]; });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var d1 = document.querySelector('.ero-starfield--depth-1');
    var d2 = document.querySelector('.ero-starfield--depth-2');

    var state = {};
    Object.keys(CONFIG.layers).forEach(function (key) {
      var cfg = CONFIG.layers[key];
      var el = document.querySelector(cfg.selector);
      if (el) state[key] = { cfg: cfg, el: el, w: 0, count: 0, iter: '' };
    });

    var lastMax = -1;

    function measure() {
      return {
        vh: window.innerHeight,
        max: Math.max(0, docEl.scrollHeight - docEl.clientHeight)
      };
    }

    // Drift inputs. Runs synchronously (never debounced): the CSS drift is
    // factor x --sf-max x scroll progress, and the browser recomputes the
    // progress from the new page length immediately, so --sf-max has to
    // change in the same frame or every layer shifts until it catches up
    // (the 6.14.28 FAQ jump). Each write restyles the page, so values are
    // only written when they change.
    function syncDrift() {
      var m = measure();
      if (m.max !== lastMax) {
        lastMax = m.max;
        docEl.style.setProperty('--sf-max', String(m.max));
        sizeTiles(d1, d2, m);
      }
      Object.keys(state).forEach(function (key) {
        var L = state[key];
        // Iterations = total drift / tile height, so each iteration moves
        // the layer exactly one tile and the wrap is seamless.
        var iter = Math.max(0.0001, (L.cfg.factor * m.max) / CONFIG.tileH).toFixed(4);
        if (iter !== L.iter) {
          L.iter = iter;
          L.el.style.setProperty('--sf-iter', iter);
        }
      });
    }

    // Tile redraw on a width change. Debounced: it is the only expensive
    // step and it never moves a star.
    function update() {
      syncDrift();
      Object.keys(state).forEach(function (key) {
        var L = state[key];
        var w = L.el.clientWidth;
        if (!L.count || w !== L.w) renderTile(L, w);
      });
    }

    update();
    logCounts(state);

    var timer = null;
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(update, 150);
    }
    function onResize() {
      syncDrift();
      schedule();
    }
    if (typeof ResizeObserver !== 'undefined') {
      // ResizeObserver callbacks run after layout and before paint, so the
      // syncDrift write lands in the same frame as the height change.
      new ResizeObserver(onResize).observe(document.body);
    }
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', onResize, { once: true });

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && !cssEngine()) {
      bindFallbackScroll(d1, d2, state);
    }
  }

  // Must match the @supports gate in ero-starfield.css exactly, so a browser
  // runs either the CSS engine or this file's fallback, never both or none.
  function cssEngine() {
    return !!(window.CSS && CSS.supports &&
      CSS.supports('animation-timeline: scroll()'));
  }

  // Seeded PRNG: mulberry32. Same seed, same sequence, every visit.
  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function hash(a, b, c, d) {
    var h = (a ^ 0x9e3779b9) | 0;
    h = Math.imul(h ^ b, 0x85ebca6b);
    h = Math.imul(h ^ c, 0xc2b2ae35);
    h = Math.imul(h ^ d, 0x27d4eb2f);
    h ^= h >>> 15;
    return h >>> 0;
  }

  function lerp(a, b, t) { return a + (b - a) * t; }

  function pickTint(t) {
    var target = t * tintTotal;
    for (var i = 0; i < CONFIG.tints.length; i++) {
      target -= CONFIG.tints[i][1];
      if (target < 0) return CONFIG.tints[i][0];
    }
    return CONFIG.tints[0][0];
  }

  // Draw the layer's stars once into a small SVG tile and use it as a
  // repeating background. A halo that would cross the tile edge is skipped
  // so the repeat seam never cuts one. Every star consumes the same number
  // of random draws whatever the settings, so turning color on or off never
  // moves a star.
  function renderTile(L, layerW) {
    var cfg = L.cfg;
    var cell = CONFIG.cell;
    var tw = Math.max(512, Math.ceil(layerW / 256) * 256);
    var th = CONFIG.tileH;
    var cols = Math.ceil(tw / cell);
    var rows = Math.ceil(th / cell);
    var expected = (cfg.density * cell * cell) / 100000;
    var parts = [];
    var halos = [];
    var count = 0;

    for (var cy = 0; cy < rows; cy++) {
      for (var cx = 0; cx < cols; cx++) {
        var rand = mulberry32(hash(CONFIG.seed, cfg.index, cx, cy));
        var n = Math.floor(expected) + (rand() < expected % 1 ? 1 : 0);
        for (var i = 0; i < n; i++) {
          var x = cx * cell + rand() * cell;
          var y = cy * cell + rand() * cell;
          var pick = rand();
          var rT = rand();
          var oT = rand();
          var tintT = rand();
          rand();
          rand();
          if (x > tw || y > th) continue;

          var row = cfg.mix[cfg.mix.length - 1];
          var acc = 0;
          for (var k = 0; k < cfg.mix.length; k++) {
            acc += cfg.mix[k][0];
            if (pick < acc) { row = cfg.mix[k]; break; }
          }
          var r = lerp(row[1], row[2], rT);
          var o = lerp(row[3], row[4], oT);
          var fill = (CONFIG.color && o >= CONFIG.colorMinOpacity) ? pickTint(tintT) : '';
          parts.push('<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) +
            '" r="' + r.toFixed(2) + '" fill-opacity="' + o.toFixed(2) + '"' +
            (fill ? ' fill="' + fill + '"' : '') + '/>');
          count++;
          var edge = CONFIG.haloScale * r + 2;
          if (r >= CONFIG.haloMinRadius && x > edge && x < tw - edge && y > edge && y < th - edge) {
            halos.push('<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) +
              '" r="' + (r * CONFIG.haloScale).toFixed(2) + '" fill="url(#h)" opacity="' + CONFIG.haloAlpha + '"/>');
          }
        }
      }
    }

    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + tw + '" height="' + th +
      '" viewBox="0 0 ' + tw + ' ' + th + '" fill="#ffffff">' +
      '<defs><radialGradient id="h">' +
      '<stop offset="0" stop-color="#ffffff" stop-opacity="1"/>' +
      '<stop offset="0.35" stop-color="#ffffff" stop-opacity="0.35"/>' +
      '<stop offset="1" stop-color="#ffffff" stop-opacity="0"/>' +
      '</radialGradient></defs>' + halos.join('') + parts.join('') + '</svg>';

    // encodeURIComponent leaves ( ) unescaped; escape them so the data URI
    // survives any CSS url() parser.
    var uri = encodeURIComponent(svg).replace(/\(/g, '%28').replace(/\)/g, '%29');
    L.el.style.backgroundImage = 'url("data:image/svg+xml,' + uri + '")';
    L.el.style.backgroundSize = tw + 'px ' + th + 'px';
    L.w = layerW;
    L.count = count;
  }

  function sizeTiles(d1, d2, m) {
    var base = Math.ceil(m.vh * 0.4);
    if (d1) d1.style.bottom = -(base + Math.ceil(m.max * CONFIG.tileFactors.d1)) + 'px';
    if (d2) d2.style.bottom = -(base + Math.ceil(m.max * CONFIG.tileFactors.d2)) + 'px';
  }

  // Fallback only: browsers without CSS scroll-driven animations.
  function bindFallbackScroll(d1, d2, state) {
    var latestY = 0;
    var ticking = false;
    function frame() {
      if (d1) d1.style.transform = 'translate3d(0,' + (-latestY * CONFIG.tileFactors.d1) + 'px,0)';
      if (d2) d2.style.transform = 'translate3d(0,' + (-latestY * CONFIG.tileFactors.d2) + 'px,0)';
      Object.keys(state).forEach(function (key) {
        var L = state[key];
        var off = (latestY * L.cfg.factor) % CONFIG.tileH;
        L.el.style.transform = 'translate3d(0,' + (-off) + 'px,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      latestY = window.scrollY;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(frame);
      }
    }, { passive: true });
  }

  function logCounts(state) {
    if (!window.console || !console.info) return;
    var parts = Object.keys(state).map(function (key) {
      return key + ' ' + state[key].count + ' stars per tile';
    });
    console.info('[ero-starfield] ' + parts.join(', ') +
      ' | color ' + (CONFIG.color ? 'on' : 'off') +
      ' | motion ' + (cssEngine() ? 'css' : 'js'));
  }
})();
